/**
 * DISTRIBUTOR-ORDERS MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * monthDate — `orders.target_ship_month` is a DATE pinned to the first day of
 * the target month (migration 0085, backlog 237). These helpers move between
 * that stored form, the `YYYY-MM` an <input type="month"> speaks, and the
 * "January 2026" a human reads.
 *
 * ⚠️ EVERY FUNCTION HERE IS STRING ARITHMETIC. Not one of them constructs a
 * `Date`, and none should start. `new Date("2026-01-01")` parses as UTC
 * midnight, so `toLocaleDateString()` in any negative-offset timezone — i.e.
 * every US user — renders it as December 31st of the previous year. That exact
 * bug shipped once already in this app (backlog 342) on a bare YYYY-MM-DD
 * string. A month label that is off by one month, and sometimes by a year, is
 * the last thing a forecast comparison needs.
 */

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** "2026-01-01" (or a Date-ish string) → "2026-01". Null-safe. */
export function toMonthKey(value) {
  if (!value) return "";
  return String(value).slice(0, 7);
}

/** "2026-01" or "2026-01-01" → "2026-01-01", the storable form. Null-safe. */
export function toMonthStart(value) {
  const key = toMonthKey(value);
  return key.length === 7 ? `${key}-01` : "";
}

/** "2026-01-01" → "January". Null-safe; unknown input returns "". */
export function monthNameOf(value) {
  const key = toMonthKey(value);
  if (key.length !== 7) return "";
  return MONTH_NAMES[Number(key.slice(5, 7)) - 1] ?? "";
}

/** "2026-01-01" → 2026, or null. */
export function yearOf(value) {
  const key = toMonthKey(value);
  return key.length === 7 ? Number(key.slice(0, 4)) : null;
}

/** "2026-01-01" → "January 2026". Null-safe; unknown input returns "". */
export function monthLabel(value) {
  const key = toMonthKey(value);
  const name = monthNameOf(key);
  return name ? `${name} ${key.slice(0, 4)}` : "";
}

/** Month name + year → "2026-01-01". Used to turn the comparison tab's
 *  {month:"January", year:2026} window into stored form. */
export function monthStartFromNameYear(name, year) {
  const i = MONTH_NAMES.indexOf(name);
  if (i < 0 || !year) return "";
  return `${year}-${String(i + 1).padStart(2, "0")}-01`;
}

/** Inclusive [first, last] month-start bounds for a list of month-start
 *  strings — so a query can use a range instead of an IN list. */
export function monthRange(monthStarts) {
  const clean = (monthStarts || []).filter(Boolean).map(toMonthStart).filter(Boolean).sort();
  if (!clean.length) return null;
  return { from: clean[0], to: clean[clean.length - 1] };
}
